import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";

const csvEscape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

const buildHospitalsCsv = (rows) => {
    const header = "Polygon,Name,Municipality,Facility Type,Type,Latitude,Longitude";
    const lines = rows.map((r) =>
        [csvEscape(r.polygon), csvEscape(r.name), csvEscape(r.municipality), csvEscape(r.facilityType), csvEscape(r.codeType), r.lat, r.lon].join(",")
    );
    return [header, ...lines].join("\n");
};

const buildInfrastructureCsv = (rows) => {
    const header = "Polygon,Type,Latitude,Longitude";
    const lines = rows.map((r) => [csvEscape(r.polygon), csvEscape(r.type), r.lat, r.lon].join(","));
    return [header, ...lines].join("\n");
};

const downloadCsv = (csv, filename) => {
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename.toLowerCase().endsWith(".csv") ? filename : `${filename}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
};

const hospitalRowsFor = (m) => m.hospitals.map((h) => ({ polygon: `Polygon ${m.id}`, ...h }));
const infraRowsFor = (m) => m.infrastructure.map((i) => ({ polygon: `Polygon ${m.id}`, ...i }));

// Exports the full matched hospital/infrastructure records (not just counts)
// for each drawn polygon — always as two separate CSVs (Hospitals,
// Infrastructure never share a file). "Combined" bundles every polygon into
// one Hospitals.csv + one Infrastructure.csv tagged with a Polygon column;
// "Separately" asks a name per polygon and exports that polygon's own pair
// of files under it. Same Blob/createObjectURL export pattern already used
// by CommonTable.js's exportToCSV.
const PolygonDownloadModal = ({ show, onHide, polygonMatches }) => {
    const [mode, setMode] = useState("combined");
    const [filename, setFilename] = useState("");
    const [polygonFilenames, setPolygonFilenames] = useState({});

    useEffect(() => {
        if (show) {
            setMode("combined");
            setFilename("");
            const defaults = {};
            polygonMatches.forEach((m) => { defaults[m.id] = `Polygon ${m.id}`; });
            setPolygonFilenames(defaults);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    const handleDownload = () => {
        if (mode === "separate") {
            polygonMatches.forEach((m) => {
                const name = (polygonFilenames[m.id] || "").trim() || `Polygon ${m.id}`;
                downloadCsv(buildHospitalsCsv(hospitalRowsFor(m)), `${name}_Hospitals.csv`);
                downloadCsv(buildInfrastructureCsv(infraRowsFor(m)), `${name}_Infrastructure.csv`);
            });
        } else {
            const name = filename.trim() || "polygon-data";
            const hospitalRows = polygonMatches.flatMap(hospitalRowsFor);
            const infraRows = polygonMatches.flatMap(infraRowsFor);
            downloadCsv(buildHospitalsCsv(hospitalRows), `${name}_Hospitals.csv`);
            downloadCsv(buildInfrastructureCsv(infraRows), `${name}_Infrastructure.csv`);
        }

        onHide();
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>Download Polygon Data</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="text-muted" style={{ fontSize: "13px" }}>
                    Exports two files — every matched hospital in one CSV, every matched infrastructure item in the other.
                </p>
                {polygonMatches.length > 1 && (
                    <Form.Group className="mb-3">
                        <Form.Label>How should the polygons be downloaded?</Form.Label>
                        <Form.Check
                            type="radio"
                            id="polygon-download-combined"
                            name="polygon-download-mode"
                            label="Combined — all polygons in one Hospitals.csv + one Infrastructure.csv"
                            checked={mode === "combined"}
                            onChange={() => setMode("combined")}
                        />
                        <Form.Check
                            type="radio"
                            id="polygon-download-separate"
                            name="polygon-download-mode"
                            label={`Separately — a Hospitals/Infrastructure pair per polygon (${polygonMatches.length * 2} files)`}
                            checked={mode === "separate"}
                            onChange={() => setMode("separate")}
                        />
                    </Form.Group>
                )}

                {mode === "separate" && polygonMatches.length > 1 ? (
                    polygonMatches.map((m) => (
                        <Form.Group className="mb-2" key={m.id}>
                            <Form.Label>File name for Polygon {m.id}</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder={`e.g. Polygon ${m.id}`}
                                value={polygonFilenames[m.id] || ""}
                                onChange={(e) => setPolygonFilenames((prev) => ({ ...prev, [m.id]: e.target.value }))}
                            />
                        </Form.Group>
                    ))
                ) : (
                    <Form.Group>
                        <Form.Label>File name</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="e.g. dili-survey"
                            value={filename}
                            onChange={(e) => setFilename(e.target.value)}
                            autoFocus
                        />
                    </Form.Group>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>Cancel</Button>
                <Button variant="primary" onClick={handleDownload} disabled={polygonMatches.length === 0}>Download</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default PolygonDownloadModal;
